import {
  HTML_ATTR_REPLACE_VAR,
  HTML_ATTR_ACTION,
  HTML_ATTR_RENDERIF,
} from '../../../const/html';
import {
  setHtmlContentMultiple,
  ElemOptions,
  Elem,
  setHtmlContent,
  negativeProperty,
} from '../../../services/html';
import {LoadingVisController} from '../../../htmlHelpers/loadingVisController';
import {
  ApiContractBlueprintGroupWithPerkbase,
  ApiContractBlueprintWithPerk,
} from '../../../services/purchase';
import {Infobox} from '../../../htmlHelpers/infobox';
import {ApiError} from '../../../services/api';
import {FormatHelper} from '../../../services/formatHelper';
import {
  PRODUCT_SELECT_ACTIONS_HTML_BINDING,
  PRODUCT_SELECT_HTML_BINDING,
} from '../../../const/htmlBindings';
import {ITranslateService} from '../../../services/translateService';
import {ApiContractBlueprintGroupV3} from '../../../../types/api/getContractBlueprints';

export interface RenderProductsParams {
  blueprintGroups: ApiContractBlueprintGroupWithPerkbase[];
  actions: {
    selectProductAndRedirect: SelectProductAction;
    selectProduct: SelectProductAction;
  };
}

type SelectProductAction = (
  group: ApiContractBlueprintGroupWithPerkbase,
  blueprint: ApiContractBlueprintWithPerk,
) => void;

interface NodeInfo {
  tag: string;
  classes: string;
  text: string;
  varReplace: string;
  action: string;
  renderIf: string;
  children: NodeInfo[];
}

// ┌──────────────────┐
// │sc-loading-hidden │ <-- loadingVisibilityController
// │ ┌────────────────┤
// │ │sc-lp-products  │ <-- productsContainer
// │ │  ┌─────────────┤
// │ │  │sc-lp-product│ <-- productBox
// │ │  ├─────────────┤
// │ │  │sc-lp-product│ <-- productBox
// │ │  ├─────────────┤
// │ │  │sc-lp-product│ <-- productBox
// └─┴──┴─────────────┘
//
// properties available for `.sc-lp-product`
//  - data-sc-var="prod-name"
//  - data-sc-var="prod-bp-name"
//  - data-sc-var="prod-badge"
//  - data-sc-var="prod-duration-count"
//  - data-sc-var="prod-duration-text"
//  - data-sc-var="prod-periodprice"
//  - data-sc-var="prod-period-text"
//  - data-sc-var="prod-perk-percent"
//  - data-sc-var="prod-calc-price-month"
//  - data-sc-var="prod-price-before-discount"
//  - data-sc-var="prod-price-before-discount-monthly"
//
//  - data-sc-render="prod-hasperk"
//  - data-sc-render="prod-hastrial"
//  - data-sc-render="prod-hasdiscount"
//
//  - data-sc-action="prod-select"
export class ProductsSelector {
  public readonly container: HTMLElement;
  private loadingVisibilityController!: LoadingVisController;
  private loadingErrorContainer!: HTMLElement;
  private productsContainer!: HTMLElement;
  private productBox!: HTMLElement;
  private productBoxTree!: NodeInfo;

  constructor(
    container: HTMLElement,
    private translateService: ITranslateService,
  ) {
    this.container = container;
    this.analyzeTemplate();
  }

  private analyzeTemplate(): void {
    // setup visibility controller
    const loadingVisible = this.container.querySelector(
      '.sc-loading-visible',
    ) as HTMLElement;
    const loadingHidden = this.container.querySelector(
      '.sc-loading-hidden',
    ) as HTMLElement;
    this.loadingErrorContainer = this.container.querySelector(
      '.sc-loading-error',
    ) as HTMLElement;
    this.loadingVisibilityController = new LoadingVisController(
      loadingVisible,
      loadingHidden,
      this.loadingErrorContainer,
    );

    // find products container:
    this.productsContainer = this.container.querySelector(
      '.sc-lp-products',
    ) as HTMLElement;

    // find product box:
    this.productBox = this.container.querySelector(
      '.sc-lp-product',
    ) as HTMLElement;

    // template info missing checks
    if (!this.productsContainer) {
      console.log(this.container);
      throw new Error(
        'skipping analyze of products container template - products container missing',
      );
    }
    if (!this.productBox) {
      console.log(this.container);
      throw new Error(
        'skipping analyze of products container template - product box missing',
      );
    }

    this.loadingVisibilityController.startLoading();

    const tree = this.analyzeNode(this.productBox);

    this.productBoxTree = tree;
  }

  public startLoadingState(): void {
    this.loadingVisibilityController.startLoading();
  }

  public setErrorMessage(msg: string): void {
    setHtmlContent(this.loadingErrorContainer, Infobox(msg, true));
    this.loadingVisibilityController.finishLoading(false);
  }

  public async setError(error: string | Error | ApiError): Promise<void> {
    if (error instanceof ApiError) {
      const apiErr = error as ApiError;
      let infobox: HTMLElement = Infobox(
        apiErr.message,
        true,
        apiErr.executionId,
      );
      switch (apiErr.code as string) {
        case 'payment_unavailableAccountAlreadySubscription':
        case 'payment/unavailableAlreadySubscription':
          infobox = Infobox(
            await this.translateService.getByKey(
              'payment.errorUnavailableAlreadySubscription',
              'Congratulations! You already have a running subscription!',
            ),
            false,
          );
          setHtmlContent(this.loadingErrorContainer, infobox);
          this.loadingVisibilityController.finishLoading(false);
          break;
        case 'payment_unavailableVariant':
        case 'payment/unavailableVariant': {
          this.setErrorMessage(
            await this.translateService.getByKey(
              'payment.errorUnavailableVariant',
              'This code is invalid. Please check your input.',
            ),
          );
          break;
        }
        default: {
          setHtmlContent(this.loadingErrorContainer, infobox);
          this.loadingVisibilityController.finishLoading(false);
        }
      }
    } else {
      this.setErrorMessage(error instanceof Error ? error.message : error);
    }
  }

  private analyzeNode(node: HTMLElement): NodeInfo {
    const children: NodeInfo[] = [];
    for (let i = 0; i < node.children.length; i++) {
      const childNode = node.children.item(i) as HTMLElement;
      if (childNode) {
        children.push(this.analyzeNode(childNode));
      }
    }

    const nodeInfo: NodeInfo = {
      tag: node.tagName,
      classes: node.className,
      // innerText is not a standard property and in tests there may be a problem
      // using and verifying it.
      text: children.length === 0 ? node.innerText : '',
      varReplace: node.getAttribute(HTML_ATTR_REPLACE_VAR) ?? '',
      action: node.getAttribute(HTML_ATTR_ACTION) ?? '',
      renderIf: node.getAttribute(HTML_ATTR_RENDERIF) ?? '',
      children,
    };

    return nodeInfo;
  }

  public renderProducts({
    blueprintGroups,
    actions,
  }: RenderProductsParams): void {
    // render new products
    this.renderProductsInTemplate(blueprintGroups, actions);
    this.loadingVisibilityController.finishLoading();
  }

  private renderProductsInTemplate(
    bpGroups: ApiContractBlueprintGroupWithPerkbase[],
    actions: {
      selectProduct: SelectProductAction;
      selectProductAndRedirect: SelectProductAction;
    },
  ): void {
    const groupContainers: HTMLElement[] = [];

    for (const blueprintGroup of bpGroups) {
      const productBoxes: HTMLElement[] = [];

      for (const blueprint of blueprintGroup.contractBlueprints) {
        const box = this.renderBoxNodeFromTemplate(
          blueprintGroup,
          blueprint,
          this.productBoxTree,
          actions,
        );
        if (box !== null) {
          productBoxes.push(box);
        }
      }

      // currently only one group is really supported...
      groupContainers.push(...productBoxes);
    }

    setHtmlContentMultiple(this.productsContainer, groupContainers);
  }

  private renderBoxNodeFromTemplate(
    group: ApiContractBlueprintGroupV3,
    bp: ApiContractBlueprintWithPerk,
    templateNode: NodeInfo,
    actions: {
      selectProduct: SelectProductAction;
      selectProductAndRedirect: SelectProductAction;
    },
  ): HTMLElement | null {
    let text = templateNode.text;
    const varReplace = templateNode.varReplace;
    const renderIf = templateNode.renderIf;

    if (varReplace === PRODUCT_SELECT_HTML_BINDING.productName) {
      text = group.name;
    }

    if (varReplace === PRODUCT_SELECT_HTML_BINDING.productBlueprintName) {
      if (!bp.name) return null;

      text = bp.name;
    }

    if (varReplace === PRODUCT_SELECT_HTML_BINDING.badge) {
      if (!bp.badge) return null;

      text = bp.badge;
    }

    if (renderIf === PRODUCT_SELECT_HTML_BINDING.hasTrial) {
      if (bp.price.firstPeriod?.periodPrice !== 0) {
        return null;
      }
    }

    if (renderIf === negativeProperty(PRODUCT_SELECT_HTML_BINDING.hasTrial)) {
      if (bp.price.firstPeriod?.periodPrice === 0) {
        return null;
      }
    }

    if (renderIf === PRODUCT_SELECT_HTML_BINDING.hasPerk) {
      if (bp.perkPercent === undefined) {
        return null;
      }
    }

    if (renderIf === negativeProperty(PRODUCT_SELECT_HTML_BINDING.hasPerk)) {
      if (bp.perkPercent !== undefined) {
        return null;
      }
    }

    if (renderIf === PRODUCT_SELECT_HTML_BINDING.hasDiscount) {
      if (bp.price.discount === null) {
        return null;
      }
    }
    if (
      renderIf === negativeProperty(PRODUCT_SELECT_HTML_BINDING.hasDiscount)
    ) {
      if (bp.price.discount !== null) {
        return null;
      }
    }

    if (varReplace === PRODUCT_SELECT_HTML_BINDING.durationCount) {
      text = bp.price.displayPeriod.periodIntervalCount?.toString() ?? '';
    }

    if (varReplace === PRODUCT_SELECT_HTML_BINDING.durationText) {
      text =
        bp.price.displayPeriod.periodIntervalCount === 1 ? 'Monat' : 'Monate';
    }

    if (varReplace === PRODUCT_SELECT_HTML_BINDING.periodPrice) {
      text = FormatHelper.instance.getPriceFormatted(
        bp.price.displayPeriod.periodPrice,
        bp.price.currency,
      );
    }

    if (varReplace === PRODUCT_SELECT_HTML_BINDING.periodText) {
      text =
        bp.price.displayPeriod.periodIntervalCount === 1
          ? 'monatlich'
          : `alle ${bp.price.displayPeriod.periodIntervalCount} Monate`;
    }

    if (varReplace === PRODUCT_SELECT_HTML_BINDING.periodPriceBeforeDiscount) {
      const originalPrice =
        bp.price.discount !== null &&
        bp.price.discount.normalPrice.displayPeriodPrice !== null
          ? bp.price.discount.normalPrice.displayPeriodPrice
          : null;
      if (originalPrice) {
        text = FormatHelper.instance.getPriceFormatted(
          originalPrice,
          bp.price.currency,
        );
      }
    }

    if (
      varReplace ===
      PRODUCT_SELECT_HTML_BINDING.periodMonthlyPriceBeforeDiscount
    ) {
      const originalPrice =
        bp.price.discount !== null &&
        bp.price.discount.normalPrice.displayPeriodSingleIntervalPrice !== null
          ? bp.price.discount.normalPrice.displayPeriodSingleIntervalPrice
          : null;
      if (originalPrice) {
        text = FormatHelper.instance.getPriceFormatted(
          originalPrice,
          bp.price.currency,
        );
      }
    }

    if (varReplace === PRODUCT_SELECT_HTML_BINDING.perkPercent) {
      text = bp.perkPercent ? bp.perkPercent.toString() : '';
    }

    if (varReplace === PRODUCT_SELECT_HTML_BINDING.periodPricePerMonth) {
      text = FormatHelper.instance.getPriceFormatted(
        bp.price.displayPeriod.periodSingleIntervalPrice,
        bp.price.currency,
      );
    }

    const options: ElemOptions = {
      class:
        templateNode.classes +
        (bp.highlight === true ? ' sc-prod-highlight' : ''),
      text,
      children: templateNode.children.map(templateNodeChild =>
        this.renderBoxNodeFromTemplate(group, bp, templateNodeChild, actions),
      ),
    };

    if (
      templateNode.action === PRODUCT_SELECT_ACTIONS_HTML_BINDING.selectProduct
    ) {
      options.onClick = () => actions.selectProduct(group, bp);
    }

    if (
      templateNode.action ===
      PRODUCT_SELECT_ACTIONS_HTML_BINDING.selectProductWithRedirect
    ) {
      options.onClick = () => actions.selectProductAndRedirect(group, bp);
    }

    return Elem(templateNode.tag, options);
  }
}
